import { createSlice } from "@reduxjs/toolkit";
import { BaseReasonDTO } from "../../models/Reason.model";
import {
  getBaseLostReasons,
  createBaseLostReason,
  deleteBaseLostReason,
  setProjectLostReason,
} from "../thunks/reasonThunks";

interface ReasonState {
  reasons: BaseReasonDTO[];
  loading: boolean;
  error: string | null;
}

const initialState: ReasonState = {
  reasons: [],
  loading: false,
  error: null,
};

const reasonSlice = createSlice({
  name: "reason",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Base Lost Reasons
      .addCase(getBaseLostReasons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBaseLostReasons.fulfilled, (state, action) => {
        state.loading = false;
        state.reasons = action.payload.reasons;
      })
      .addCase(getBaseLostReasons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to get reasons";
      })
      // Create Base Lost Reason
      .addCase(createBaseLostReason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBaseLostReason.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          // Add new reason to the list if creation was successful
          const newReason: BaseReasonDTO = {
            lostReason_ID: action.payload.baseLostReasonID,
            lostReason_Name: action.meta.arg.baseLostReasonName,
          };
          state.reasons.push(newReason);
        }
      })
      .addCase(createBaseLostReason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create reason";
      })
      // Delete Base Lost Reason
      .addCase(deleteBaseLostReason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBaseLostReason.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          // Remove deleted reason from the list
          state.reasons = state.reasons.filter(
            (reason) => reason.lostReason_ID !== action.meta.arg.baseLostReasonID
          );
        }
      })
      .addCase(deleteBaseLostReason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete reason";
      })
      // Set Project Lost Reason
      .addCase(setProjectLostReason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setProjectLostReason.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setProjectLostReason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to set project reason";
      });
  },
});

export const { clearError } = reasonSlice.actions;
export default reasonSlice.reducer;